import React, { useState, useEffect } from 'react';
import { Editor } from '@tiptap/react';
import WorkbookImageUploadModal from '@/components/ui/upload/WorkbookImageUploadModal';

interface InsertWorkbookImageDialogProps {
  editor: Editor;
  isOpen: boolean;
  onClose: () => void;
  workbookId: string;
  pageId?: string;
}

export function InsertWorkbookImageDialog({ 
  editor, 
  isOpen, 
  onClose, 
  workbookId, 
  pageId 
}: InsertWorkbookImageDialogProps) {
  const [cursorPosition, setCursorPosition] = useState<{ from: number; to: number } | null>(null);

  // Save cursor position when modal opens
  useEffect(() => {
    if (isOpen && editor) {
      const { from, to } = editor.state.selection;
      setCursorPosition({ from, to });
    }
  }, [isOpen, editor]);

  const handleImageUploaded = (imageUrl: string, altText?: string) => {
    if (!editor) return;

    // Simple image insertion - just insert the image and close modal
    const hasResizableImage = editor.extensionManager.extensions.some(ext => ext.name === 'resizableImage');
    
    if (hasResizableImage) {
      editor.commands.setResizableImage({ 
        src: imageUrl,
        alt: altText || 'Workbook image'
      });
    } else {
      editor.commands.setImage({ 
        src: imageUrl,
        alt: altText || 'Workbook image'
      });
    }

    onClose();
  };

  // NEW: Direct image import handler for existing images (avoids duplication)
  const handleDirectImageImport = (imageUrl: string, altText?: string) => {
    console.log('[InsertWorkbookImageDialog] Direct image import:', { imageUrl, altText });
    // Use the same insertion logic as uploaded images
    handleImageUploaded(imageUrl, altText);
  };

  const handleClose = () => {
    setCursorPosition(null);
    onClose();
  };

  return (
    <WorkbookImageUploadModal
      isOpen={isOpen}
      onClose={handleClose}
      onImageUploaded={handleImageUploaded}
      onDirectImageImport={handleDirectImageImport} // NEW: Enable direct URL insertion for imports
      workbookId={workbookId}
      pageId={pageId}
    />
  );
} 